import React, { useEffect, useState } from 'react';
import Sidebar from 'components/sidebar';
import Header from 'components/header';
import './styles.scss';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import BirthdayReminder from 'components/birthday-reminder';
import { ROUTE_KEYS } from 'utils/constants';
import { apiService } from 'services/base.service';
import PageLoader from 'components/base/page-loader';
import FeedbackModal from 'pages/feedback';
import { ReactComponent as HomeIcon } from 'assets/icons/home.svg';
import { ReactComponent as DashboardIcon } from 'assets/icons/dashboard.svg';
import { ReactComponent as EventIcon } from 'assets/icons/events.svg';
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg';
import { ReactComponent as OrderIcon } from 'assets/icons/orders.svg';
import { ReactComponent as FeedbackIcon } from 'assets/icons/feedback.svg';
import { ReactComponent as HelpIcon } from 'assets/icons/question-mark.svg';
import { RmxProcurementIcon as PaymentIcon } from 'cokitchen-icons';
import { handleApiError, isOnboardingCompleted } from 'utils/functions';
import LowBalanceModal from 'components/modals/low-balance-modal';
import PillAlerts from 'components/pill-alerts';
import { MemberListType } from 'models/member.model';

const DashboardContent = () => {
  const navigate = useNavigate();
  const {
    hideSidebar,
    showFeedbackModal,
    setShowFeedbackModal,
    backLinkTitle,
    backLink,
    needHelp,
    activeCompany,
    setCompanyList,
    setSidebarMenu,
    onboarding,
    refetchOnboarding
  } = useDashboardContext();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [birthdays, setBirthdays] = useState<MemberListType[]>([]);
  const [loading, setLoading] = useState(true);

  const url = pathname.split('/');
  const isBrandPage = url[2] === ROUTE_KEYS.RESTAURANTS && url.length === 4;
  const isHomeRoute =
    pathname.split('/').length === 2 && pathname.split('/')[1] === ROUTE_KEYS.HOME;
  const isDashboardRoute =
    pathname.split('/').length === 2 && pathname.split('/')[1] === ROUTE_KEYS.DASHBOARD;
  const isPaymentRoute = pathname.startsWith('/payment');
  const isDashboardOrOrdersRoute =
    pathname.split('/').length === 2 &&
    (pathname.split('/')[1] === ROUTE_KEYS.DASHBOARD ||
      pathname.split('/')[1] === ROUTE_KEYS.ORDERS);

  const showWalletBallance = [isHomeRoute, isDashboardRoute, isPaymentRoute].some(Boolean);

  const SIDEBAR_LINKS = [
    {
      name: 'Getting Started',
      icon: <HomeIcon className='w-5 h-5' />,
      route: ROUTE_KEYS.GET_STARTED
    },
    {
      name: 'Dashboard',
      icon: <DashboardIcon className='w-5 h-5' />,
      route: ROUTE_KEYS.DASHBOARD
    },
    {
      name: 'Members',
      icon: <PeopleIcon className='w-5 h-5' />,
      route: ROUTE_KEYS.PEOPLE
    },
    {
      name: 'Orders',
      icon: <OrderIcon className='w-5 h-5' />,
      route: ROUTE_KEYS.ORDERS
    },
    {
      name: 'Events',
      icon: <EventIcon className='w-5 h-5' />,
      route: ROUTE_KEYS.EVENTS,
      underline: true
    },
    {
      name: 'Payments',
      icon: <PaymentIcon className='w-5 h-5' />,
      route: ROUTE_KEYS.PAYMENT_GENERAL
    },
    {
      name: 'Feedback',
      icon: <FeedbackIcon className='w-5 h-5' />,
      onClick: () => setShowFeedbackModal(true)
    },
    {
      name: 'Support',
      icon: <HelpIcon className='w-5 h-5' />,
      route: ROUTE_KEYS.SUPPORT
    }
  ];

  const loadCompanies = async () => {
    try {
      const res = await apiService.get('company');
      setCompanyList(res.data.data);
    } catch (err) {
      handleApiError(err);
    }
  };

  const loadUpcomingBirthday = async () => {
    try {
      if (!activeCompany.id) return;
      const res = await apiService.get(`admin/upcoming-birthday/${activeCompany.id}`);
      setBirthdays(res.data.data);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    refetchOnboarding();
    loadUpcomingBirthday();
  }, [activeCompany]);

  useEffect(() => {
    if (isOnboardingCompleted(onboarding)) {
      setSidebarMenu(
        SIDEBAR_LINKS.map((item, index) => {
          if (index === 0) return { ...item, name: 'Home', route: ROUTE_KEYS.HOME };
          return item;
        })
      );
    } else {
      setSidebarMenu([...SIDEBAR_LINKS]);
      if (
        !pathname.startsWith('/get-started') &&
        !(searchParams.get('from') === ROUTE_KEYS.GET_STARTED)
      )
        navigate(ROUTE_KEYS.GET_STARTED);
    }
    setLoading(!onboarding.length);
  }, [onboarding, pathname]);

  return (
    <div className='relative flex'>
      {!hideSidebar && (
        <div className='max-lg:hidden'>
          <Sidebar />
        </div>
      )}
      <div className={`flex flex-col grow ${hideSidebar ? 'full' : ' part'}`}>
        <Header title={backLinkTitle} backLink={backLink} needHelp={needHelp} />

        <div className='flex flex-col grow body_main'>
          {isHomeRoute && birthdays.length > 0 && <BirthdayReminder birthdays={birthdays} />}
          {showWalletBallance && <LowBalanceModal />}

          {activeCompany?.id && isDashboardOrOrdersRoute && <PillAlerts />}
          <div className={`w-full ${!isBrandPage && 'body'}`}>
            {activeCompany?.id && !loading ? <Outlet /> : <PageLoader />}
          </div>
        </div>
      </div>
      {showFeedbackModal && <FeedbackModal show onClose={() => setShowFeedbackModal(false)} />}
    </div>
  );
};

export default DashboardContent;
